const pad = (value) => String(value).padStart(2, "0");

export function stripHtml(value = "") {
  return String(value)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value = "", max = 180) {
  const clean = stripHtml(value);

  if (clean.length <= max) return clean;

  return `${clean.slice(0, max - 1)}…`;
}

export function getDateOnlyFromInput(value = "") {
  if (!value) return "";

  return String(value).slice(0, 10);
}

export function toLocalInput(value) {
  if (!value) return "";

  const raw = String(value).trim();

  /**
   * Reçoit :
   * - 2026-05-20T10:00:00
   * - 2026-05-20 10:00:00
   *
   * Retourne :
   * - 2026-05-20T10:00
   *
   * Important : aucune conversion avec new Date().
   */
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/);

  if (match) {
    return `${match[1]}T${match[2]}`;
  }

  const dateOnlyMatch = raw.match(/^(\d{4}-\d{2}-\d{2})$/);

  if (dateOnlyMatch) {
    return `${dateOnlyMatch[1]}T00:00`;
  }

  return "";
}

export function fromLocalInputToApi(value) {
  if (!value) return null;

  const clean = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) {
    return `${clean} 00:00:00`;
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(clean)) {
    return `${clean.replace("T", " ")}:00`;
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(clean)) {
    return clean.replace("T", " ");
  }

  if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}$/.test(clean)) {
    return clean;
  }

  return null;
}

export function fromDateOnlyToApi(value, endOfDay = false) {
  if (!value) return null;

  const clean = String(value).trim();

  if (!/^\d{4}-\d{2}-\d{2}$/.test(clean)) return null;

  return endOfDay ? `${clean} 23:59:59` : `${clean} 00:00:00`;
}

export function formatDateFr(dateString = "") {
  if (!dateString) return "—";

  const raw = String(dateString).trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (!match) return "—";

  const [, yyyy, mm, dd] = match;

  return `${dd}/${mm}/${yyyy}`;
}

export function formatDateTimeFr(dateString = "") {
  if (!dateString) return "—";

  const raw = String(dateString).trim();

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/,
  );

  if (!match) return formatDateFr(raw);

  const [, yyyy, mm, dd, hh, mi] = match;

  return `${dd}/${mm}/${yyyy} à ${hh}:${mi}`;
}

export function formatEventDateParts(value) {
  if (!value) {
    return {
      day: "—",
      month: "",
      time: "",
      year: "",
    };
  }

  const raw = String(value).trim();

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/,
  );

  if (!match) {
    return {
      day: "—",
      month: "",
      time: "",
      year: "",
    };
  }

  const [, yyyy, mm, dd, hh, mi] = match;

  const months = [
    "",
    "janv",
    "févr",
    "mars",
    "avr",
    "mai",
    "juin",
    "juil",
    "août",
    "sept",
    "oct",
    "nov",
    "déc",
  ];

  return {
    day: dd,
    month: months[Number(mm)] || "",
    year: yyyy,
    time: hh && mi ? `${hh}:${mi}` : "",
  };
}

export function getEventPeriodLabel(event) {
  if (!event?.start_date) return "Date à renseigner";

  const isAllDay = !!event.all_day;

  if (isAllDay) {
    const start = formatDateFr(event.start_date);

    if (!event.end_date) return start;

    const end = formatDateFr(event.end_date);

    if (start === end) return start;

    return `Du ${start} au ${end}`;
  }

  const start = formatDateTimeFr(event.start_date);

  if (!event.end_date) return start;

  const end = formatDateTimeFr(event.end_date);

  return `${start} → ${end}`;
}

export function buildEventsSearchParams({
  q,
  categorie,
  stateFilter,
  dateFrom,
  dateTo,
  page,
  limit,
}) {
  return {
    q: q?.trim() || undefined,
    categorie: categorie?.trim() || undefined,
    is_active:
      stateFilter === "all"
        ? undefined
        : stateFilter === "active"
          ? true
          : false,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    page,
    limit,
    sort: "start_date",
    dir: "ASC",
  };
}

export function normalizeEventForm(form) {
  const isAllDay = !!form.all_day;

  const startDate = isAllDay
    ? fromDateOnlyToApi(getDateOnlyFromInput(form.start_date), false)
    : fromLocalInputToApi(form.start_date);

  const endDate = isAllDay
    ? fromDateOnlyToApi(getDateOnlyFromInput(form.end_date), true)
    : fromLocalInputToApi(form.end_date);

  return {
    title: form.title.trim(),
    subtitle: form.subtitle?.trim() || "",
    categorie: form.categorie?.trim() || "",
    description: form.description || "",
    start_date: startDate,
    end_date: endDate,
    photo_vignette: form.photo_vignette?.trim() || "",
    external_link: form.external_link?.trim() || "",
    all_day: isAllDay,
    is_active: !!form.is_active,
  };
}

export function validateEventForm(form) {
  const errors = {};
  const isAllDay = !!form.all_day;

  if (!form.title?.trim()) {
    errors.title = "Le titre est obligatoire.";
  }

  if (!form.start_date) {
    errors.start_date = "La date de début est obligatoire.";
  }

  const startDate = isAllDay
    ? fromDateOnlyToApi(getDateOnlyFromInput(form.start_date), false)
    : fromLocalInputToApi(form.start_date);

  const endDate = isAllDay
    ? fromDateOnlyToApi(getDateOnlyFromInput(form.end_date), true)
    : fromLocalInputToApi(form.end_date);

  if (form.start_date && !startDate) {
    errors.start_date = "La date de début est invalide.";
  }

  if (form.end_date && !endDate) {
    errors.end_date = "La date de fin est invalide.";
  }

  if (startDate && endDate && endDate < startDate) {
    errors.end_date = "La date de fin doit être postérieure à la date de début.";
  }

  return errors;
}

export function isUpcomingEvent(event) {
  if (!event?.start_date) return false;

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = pad(today.getMonth() + 1);
  const dd = pad(today.getDate());

  const current = `${yyyy}-${mm}-${dd}`;
  const eventDate = String(event.start_date).slice(0, 10);

  return eventDate >= current;
}

export function getEventsStats(items = [], total = 0) {
  const activeOnPage = items.filter((item) => !!item.is_active).length;
  const inactiveOnPage = items.filter((item) => !item.is_active).length;
  const upcomingOnPage = items.filter((item) => isUpcomingEvent(item)).length;
  const withImageOnPage = items.filter((item) => !!item.photo_vignette).length;

  return {
    total,
    activeOnPage,
    inactiveOnPage,
    upcomingOnPage,
    withImageOnPage,
  };
}